import { $convertFromMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $insertNodeToNearestRoot } from '@lexical/utils';
import { isMimeType, mediaFileReader } from '@lexical/utils';
import {
  $createParagraphNode,
  COMMAND_PRIORITY_HIGH,
  PASTE_COMMAND,
  PasteCommandType,
} from 'lexical';
import { useEffect, useMemo } from 'react';
import { createTransformers } from '../config/transformers';
import { INSERT_IMAGE_COMMAND } from './ImagesPlugin';

const ACCEPTABLE_IMAGE_TYPES = ['image/', 'image/heic', 'image/heif', 'image/gif', 'image/webp'];

export default function PasteHandlerPlugin(): null {
  const [editor] = useLexicalComposerContext();
  const transformers = useMemo(() => createTransformers(editor), [editor]);

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event: PasteCommandType) => {
        if (event.type !== 'paste') {
          return false;
        }

        const clipboardEvent = event as ClipboardEvent;
        const clipboardData = clipboardEvent.clipboardData;
        if (!clipboardData) {
          return false;
        }

        const files = Array.from(clipboardData.files);
        if (files.length > 0) {
          (async () => {
            const filesResult = await mediaFileReader(
              files,
              [ACCEPTABLE_IMAGE_TYPES].flatMap(x => x),
            );
            for (const { file, result } of filesResult) {
              if (isMimeType(file, ACCEPTABLE_IMAGE_TYPES)) {
                editor.dispatchCommand(INSERT_IMAGE_COMMAND, {
                  altText: file.name,
                  src: result,
                });
              }
            }
          })();
          return true;
        }

        // $convertFromMarkdownString tries to clear the entire root node
        // that it is provided. To work around this we create a temporary node to
        // use as the root, and then remove it afterwards.
        const pastedText = clipboardData.getData('text/plain');
        const tempRoot = $createParagraphNode();
        $insertNodeToNearestRoot(tempRoot);
        $convertFromMarkdownString(pastedText, transformers, tempRoot, true);

        const parent = tempRoot.getParent();
        if (parent) {
          const children = tempRoot.getChildren();
          const tempRootIndex = parent.getChildren().indexOf(tempRoot);
          if (tempRootIndex !== -1) {
            tempRoot.remove();
            parent.splice(tempRootIndex, 0, children);
            const lastChild = children[children.length - 1];
            if (lastChild) {
              lastChild.selectEnd();
            }
          }
        }
        return true;
      },
      COMMAND_PRIORITY_HIGH,
    );
  }, [editor]);

  return null;
}
